import React from 'react';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { adminAPI } from '../../services/api';
import Panel from '../crt/Panel';
import SeverityChip from './SeverityChip';

// Map admin actions to one of the four severity colors used across the dashboard.
const ACTION_SEVERITY: Record<string, string> = {
  trigger_job: 'MAJOR',        // yellow
  enable_job: 'OK',            // green
  disable_job: 'MINOR',        // cyan
  resolve_error: 'OK',         // green
  unresolve_error: 'MINOR',
  alert_config_update: 'MINOR',// cyan
  test_alert: 'MINOR',
};

const severityFor = (action: string) => ACTION_SEVERITY[action] || 'MINOR';

const stringifyDetails = (details: any): string => {
  if (details == null) return '';
  if (typeof details === 'string') return details;
  try {
    return JSON.stringify(details);
  } catch {
    return String(details);
  }
};

const truncate = (s: string, n = 160) => (s.length > n ? s.slice(0, n) + '…' : s);

const ActionsTab: React.FC = () => {
  const { data } = useQuery({
    queryKey: ['admin-actions'],
    queryFn: () => adminAPI.getActions(200),
    staleTime: 15000,
    refetchInterval: 30000,
  });

  const actions = data?.actions || [];

  return (
    <Panel label="ADMIN ACTIONS AUDIT LOG">
      <Box sx={{ overflowX: 'auto' }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>TIMESTAMP</TableCell>
              <TableCell>ACTION</TableCell>
              <TableCell>TARGET</TableCell>
              <TableCell>ACTOR</TableCell>
              <TableCell>DETAILS</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {actions.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5}>
                  <Typography variant="caption" color="textSecondary">
                    No admin actions recorded yet.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              actions.map((a: any) => {
                const detailsStr = stringifyDetails(a.details);
                return (
                  <TableRow key={a.id} hover>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                      {a.created_at ? new Date(a.created_at).toLocaleString() : '—'}
                    </TableCell>
                    <TableCell>
                      <Box display="flex" alignItems="center" gap={1}>
                        <SeverityChip severity={severityFor(a.action)} />
                        <Typography variant="body2" fontFamily="'VT323', monospace">
                          {a.action}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight={600}>
                        {a.target || '—'}
                      </Typography>
                    </TableCell>
                    <TableCell>{a.actor || '—'}</TableCell>
                    <TableCell sx={{ maxWidth: 420 }}>
                      {detailsStr ? (
                        <Tooltip title={<pre style={{ margin: 0 }}>{detailsStr}</pre>} placement="top-start" arrow>
                          <Box
                            component="code"
                            sx={{
                              display: 'inline-block',
                              maxWidth: 420,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                              verticalAlign: 'bottom',
                              fontSize: '13px',
                              color: 'var(--crt-dim)',
                            }}
                          >
                            {truncate(detailsStr)}
                          </Box>
                        </Tooltip>
                      ) : (
                        '—'
                      )}
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </Box>
    </Panel>
  );
};

export default ActionsTab;
